import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Check, Gear, Medal } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

// Top-right notification bell with unread badge and click-to-open dropdown.
// Polls /api/notifications/unread-count every 30s while mounted, and refreshes
// the full list when the panel opens. Click a row to mark-read and navigate.

const RELATIVE_MS = { minute: 60_000, hour: 3_600_000, day: 86_400_000 };

function timeAgo(iso) {
  try {
    const t = Date.parse(iso);
    if (!t || Number.isNaN(t)) return "";
    const delta = Date.now() - t;
    if (delta < RELATIVE_MS.minute) return "just now";
    if (delta < RELATIVE_MS.hour) return `${Math.round(delta / RELATIVE_MS.minute)}m ago`;
    if (delta < RELATIVE_MS.day) return `${Math.round(delta / RELATIVE_MS.hour)}h ago`;
    return `${Math.round(delta / RELATIVE_MS.day)}d ago`;
  } catch {
    return "";
  }
}

const kindLabels = {
  message: "MESSAGE",
  program_assigned: "PROGRAM",
  workout_logged: "WORKOUT",
  coach_note: "NOTE",
  session_started: "LIVE",
  session_completed: "DONE",
};

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch {/* silent */}
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30_000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Listen for external open triggers (e.g., mobile drawer notification row)
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-notifications", openHandler);
    return () => window.removeEventListener("open-notifications", openHandler);
  }, []);

  useEffect(() => {
    if (open) refreshList();
  }, [open, refreshList]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const openItem = async (n) => {
    try {
      if (!n.is_read) {
        await api.post(`/notifications/${n.notification_id}/read`);
      }
    } catch {/* silent */}
    setOpen(false);
    if (n.link) navigate(n.link);
    refreshList();
  };

  const dismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setItems((p) => p.filter((x) => x.notification_id !== id));
      refreshCount();
    } catch { toast.error("Dismiss failed"); }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      refreshList();
      toast.success("All caught up");
    } catch { toast.error("Failed to mark read"); }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-root">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`hidden lg:flex fixed top-3 right-3 z-40 h-10 w-10 items-center justify-center border transition-colors ${open ? "!border-yellow-400 !text-yellow-400 bg-surface-elevated" : "border-white/20 text-white/80 hover:!border-yellow-400 hover:!text-yellow-400 bg-surface-elevated/80 backdrop-blur"}`}
        data-testid="notification-bell-button"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={17} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full !bg-yellow-400 !text-slate-900 text-[10px] font-bold flex items-center justify-center tabular-nums"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed top-14 right-3 z-40 w-96 max-w-[calc(100vw-24px)] max-h-[70vh] flex flex-col border !border-yellow-400/40 bg-surface-DEFAULT shadow-2xl"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/15">
            <div>
              <div className="label-tiny text-[10px] !text-yellow-400">// INBOX</div>
              <div className="font-heading text-lg text-white">Notifications</div>
            </div>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="inline-flex items-center gap-1 text-[11px] label-tiny tracking-widest text-white/70 hover:!text-yellow-400 transition-colors"
                  data-testid="notification-mark-all-read"
                >
                  <Check size={11} weight="bold" /> MARK ALL READ
                </button>
              )}
              <button
                onClick={() => { setOpen(false); navigate("/profile#notifications"); }}
                className="inline-flex items-center gap-1 text-[11px] label-tiny tracking-widest text-white/70 hover:!text-yellow-400 transition-colors"
                title="Notification preferences"
                data-testid="notification-open-preferences"
              >
                <Gear size={12} weight="bold" /> PREFS
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-sm text-white/60" data-testid="notification-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center" data-testid="notification-empty">
                <Bell size={28} weight="duotone" className="mx-auto mb-2 text-white/40" />
                <div className="text-sm text-white/70">No notifications yet</div>
                <div className="text-[11px] text-white/50 mt-1">Messages, assignments and PRs land here.</div>
              </div>
            ) : (
              items.map((n) => {
                const prCount = Number(n.meta?.new_prs) || 0;
                return (
                <div
                  key={n.notification_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openItem(n)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openItem(n); } }}
                  className={`group w-full text-left px-4 py-3 border-b border-white/10 hover:!bg-yellow-400/10 transition-colors flex gap-3 items-start cursor-pointer focus:outline-none focus:!bg-yellow-400/10 ${prCount ? "border-l-2 !border-l-yellow-400 bg-yellow-400/[0.07]" : !n.is_read ? "bg-yellow-400/5" : ""}`}
                  data-testid={`notification-row-${n.notification_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`label-tiny text-[10px] tracking-widest ${!n.is_read ? "!text-yellow-400" : "text-white/50"}`}>
                        {kindLabels[n.kind] || n.kind.toUpperCase()}
                      </span>
                      {prCount > 0 && (
                        <span
                          className="inline-flex items-center gap-1 label-tiny text-[9px] !text-yellow-400 border border-yellow-400/50 bg-yellow-400/10 px-1.5 py-0.5 leading-none"
                          data-testid={`notification-pr-tag-${n.notification_id}`}
                        >
                          <Medal size={10} weight="fill" /> {prCount > 1 ? `${prCount} PRs` : "NEW PR"}
                        </span>
                      )}
                      <span className="text-[10px] text-white/40">{timeAgo(n.created_at)}</span>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full !bg-yellow-400" data-testid={`notification-dot-${n.notification_id}`} />}
                    </div>
                    <div className="text-sm text-white font-medium truncate">{n.title}</div>
                    {n.body && <div className="text-[12px] text-white/70 line-clamp-2 mt-0.5">{n.body}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => dismiss(e, n.notification_id)}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-white/50 hover:text-red-400 shrink-0 mt-0.5"
                    aria-label="Dismiss"
                    data-testid={`notification-dismiss-${n.notification_id}`}
                  >
                    <X size={12} />
                  </button>
                </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
